import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridPaginationModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { Box } from '@mui/material';

export interface ReportTableData {
  [key: string]: any;
}

export interface ReportTableLayout {
  columns: GridColDef[];
  pageSize?: number;
  pageSizeOptions?: number[];
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  autoHeight?: boolean;
  density?: 'compact' | 'standard' | 'comfortable';
  showHeaderFilters?: boolean;
  showQuickFilter?: boolean;
}

export interface ReportTableComponentProps {
  data: ReportTableData[] | null;
  layout: ReportTableLayout | null;
  getRowId?: (row: ReportTableData) => string | number;
}

export interface ReportTableComponentRef {
  filterData: (filterModel: GridFilterModel) => void;
  clearFilters: () => void;
}

const defaultLayout: ReportTableLayout = {
  columns: [],
  pageSize: 10,
  pageSizeOptions: [10, 25, 50, 100, -1],
  enableSorting: true,
  enableFiltering: true,
  enablePagination: true,
  autoHeight: false,
  density: 'standard',
  showHeaderFilters: true,
  showQuickFilter: true,
};

// Custom toolbar component with filter button and quick filter
function CustomToolbar({ showQuickFilter = false }: { showQuickFilter?: boolean }) {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      {showQuickFilter && (
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <GridToolbarQuickFilter />
        </Box>
      )}
    </GridToolbarContainer>
  );
}

export const ReportTableComponent = forwardRef<ReportTableComponentRef, ReportTableComponentProps>(
  ({ data, layout, getRowId }, ref) => {
    const resolvedLayout = layout ?? defaultLayout;
    
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
      page: 0,
      pageSize: resolvedLayout.pageSize || 10,
    });

    useImperativeHandle(ref, () => ({
      filterData: (newFilterModel: GridFilterModel) => {
        setFilterModel(newFilterModel);
      },
      clearFilters: () => {
        setFilterModel({ items: [] });
      },
    }));

    const {
      columns,
      pageSizeOptions = [10, 25, 50, 100, -1],
      enableSorting = true,
      enableFiltering = true,
      enablePagination = true,
      autoHeight = false,
      density = 'standard',
      showHeaderFilters = false,
      showQuickFilter = false,
    } = resolvedLayout;

    // Convert pageSizeOptions to include "All" label for -1 value
    const formattedPageSizeOptions = pageSizeOptions.map((option) =>
      option === -1 ? { value: -1, label: 'All' } : option
    );

    return (
      <div style={{ width: '100%', height: autoHeight ? 'auto' : '400px' }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          getRowId={getRowId || ((row) => row.id)}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={formattedPageSizeOptions}
          disableColumnFilter={!enableFiltering}
          disableColumnMenu={!enableFiltering}
          disableColumnSelector={!enableFiltering}
          disableDensitySelector={false}
          sortingOrder={enableSorting ? ['asc', 'desc'] : []}
          disableRowSelectionOnClick={true}
          density={density}
          {...(enablePagination && { pagination: true })}
          {...(showHeaderFilters && {
            slots: {
              toolbar: CustomToolbar,
            },
            slotProps: {
              toolbar: {
                showQuickFilter,
              },
            },
          })}
          sx={{
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
          }}
        />
      </div>
    );
  }
);

ReportTableComponent.displayName = 'ReportTableComponent';

export default ReportTableComponent;
